import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Avatar,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {
  FiBriefcase,
  FiChevronDown,
  FiClock,
  FiCrosshair,
  FiEdit3,
  FiMenu,
  FiMapPin,
  FiMessageCircle,
  FiMic,
  FiPackage,
  FiSearch,
  FiBookmark,
  FiCheck,
  FiBell,
  FiShoppingCart,
  FiUser,
  FiX,
} from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import LoginForm from "../UI/LoginForm";
import { colors, gradients, radii, shadows } from "../UI/designSystem";
import { API_BASE } from "../../config";
import useNotifications from "../../hooks/useNotifications";
import { isPushSupported, getPushPermission, subscribeToPush } from "../../services/pushNotifications";

const homeRouteForRole = (role) => {
  if (role === "pharmacist") return "/pharmacist/home";
  if (role === "admin") return "/admin-home";
  return "/home";
};
const ACTIVE_ORDER_STAGES = new Set([
  "payment_pending",
  "ready_to_post",
  "delivery_quote_ready",
  "delivery_created",
  "delivery_in_progress",
]);

const WithSubnavigation = ({
  username,
  name,
  onLogout,
  notifications,
  onToggleSidebar,
  chatLink = null,
  cartCount = 0,
  sidebarOffset = "248px",
  savedAddresses = [],
  isLoggedIn = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cookies] = useCookies(["role"]);
  const homeRoute = homeRouteForRole(cookies.role);
  const [activeOrder, setActiveOrder] = useState(null);
  const [globalSearch, setGlobalSearch] = useState("");
  const { isOpen: isLoginOpen, onOpen: onLoginOpen, onClose: onLoginClose } = useDisclosure();
  const [location_, setLocation_] = useState(
    localStorage.getItem("pillstackLocation") || "",
  );
  const [locEditing, setLocEditing] = useState(false);
  const [locDraft, setLocDraft] = useState("");
  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const [placesLoading, setPlacesLoading] = useState(false);
  const [detectingGps, setDetectingGps] = useState(false);
  const [lastSelectedAddress, setLastSelectedAddress] = useState(null);
  const [savingAddress, setSavingAddress] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const toast = useToast();
  const [notifOpen, setNotifOpen] = useState(false);
  const [pushState, setPushState] = useState({ supported: false, permission: 'default', subscribing: false });
  const handleNewNotification = useCallback((n) => {
    toast({
      title: n.title || 'New notification',
      description: n.message || '',
      status: 'info',
      duration: 5000,
      isClosable: true,
      position: 'top-right',
    });
  }, [toast]);
  const {
    notifications: localNotifs,
    unreadCount: unread,
    markRead,
    markAllRead,
    deleteNotification,
  } = useNotifications({ pollMs: 30000, enabled: isLoggedIn, onNewNotification: handleNewNotification });

  useEffect(() => {
    if (isPushSupported()) {
      setPushState(prev => ({ ...prev, supported: true, permission: getPushPermission() }));
    }
  }, []);

  const handleEnablePush = useCallback(async () => {
    setPushState(prev => ({ ...prev, subscribing: true }));
    const result = await subscribeToPush();
    setPushState(prev => ({ ...prev, subscribing: false, permission: getPushPermission() }));
    toast({
      title: result.success ? 'Push notifications enabled' : 'Push notification failed',
      description: result.success ? 'You will now receive alerts on this device.' : (result.error || ''),
      status: result.success ? 'success' : 'warning',
      duration: 3000,
      isClosable: true,
    });
  }, [toast]);

  useEffect(() => {
    console.log('[push:auto] isLoggedIn', isLoggedIn, 'supported', isPushSupported(), 'permission', getPushPermission(), 'shown', localStorage.getItem('pushPromptShown'));
    if (!isLoggedIn) return;
    if (!isPushSupported()) return;
    if (getPushPermission() !== 'default') return;
    if (localStorage.getItem('pushPromptShown')) return;

    const timer = setTimeout(() => {
      subscribeToPush().then((result) => {
        localStorage.setItem('pushPromptShown', 'true');
        setPushState(prev => ({ ...prev, permission: getPushPermission() }));
        if (result.success) {
          toast({
            title: 'Push notifications enabled',
            description: 'You will now receive alerts on this device.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        } else if (getPushPermission() === 'denied') {
          toast({
            title: 'Push notifications blocked',
            description: 'Click the lock icon next to the URL, go to Site settings, and set Notifications to "Allow".',
            status: 'warning',
            duration: 10000,
            isClosable: true,
          });
        } else if (getPushPermission() === 'default') {
          toast({
            duration: 10000,
            isClosable: true,
            render: ({ onClose }) => (
              <Box color="white" p={3} bg={colors.brand[500]} borderRadius={radii.md}>
                <Text fontWeight="bold">Enable push notifications?</Text>
                <Text fontSize="sm" mb={2}>Get order alerts even when the app is closed.</Text>
                <Button
                  size="sm"
                  colorScheme="whiteAlpha"
                  onClick={async () => {
                    await handleEnablePush();
                    onClose();
                  }}
                >
                  Enable
                </Button>
              </Box>
            ),
          });
        }
      });
    }, 3000);

    return () => clearTimeout(timer);
  }, [isLoggedIn, toast, handleEnablePush]);

  useEffect(() => {
    const onLocationSync = () => {
      const fresh = localStorage.getItem("pillstackLocation");
      if (fresh) {
        setLocation_(fresh);
      }
    };
    window.addEventListener("pillstackLocationChanged", onLocationSync);
    return () => window.removeEventListener("pillstackLocationChanged", onLocationSync);
  }, []);

  const isAddressSaved = useCallback((addr) => {
    if (!addr) return true;
    return savedAddresses.some((s) => (s?.address || s).toLowerCase().trim() === addr.toLowerCase().trim());
  }, [savedAddresses]);

  const broadcastLocation = useCallback((address, coords = null) => {
    setLocation_(address);
    localStorage.setItem("pillstackLocation", address);
    if (coords) {
      localStorage.setItem("pillstackLocationCoords", JSON.stringify(coords));
    } else {
      localStorage.removeItem("pillstackLocationCoords");
    }
    window.dispatchEvent(new Event("pillstackLocationChanged"));
    setLastSelectedAddress(address);
  }, []);

  // If location is still stored as raw coordinates, convert it to a readable address
  useEffect(() => {
    const isCoords = (str) => /^-?\d+\.\d+,\s*-?\d+\.\d+$/.test(str || "");
    if (!isCoords(location_)) return;
    const rawCoords = localStorage.getItem("pillstackLocationCoords");
    if (!rawCoords) return;
    try {
      const coords = JSON.parse(rawCoords);
      if (!Array.isArray(coords) || coords.length !== 2) return;
      const [lng, lat] = coords;
      axios
        .get(`${API_BASE}/api/mappls/rev-geocode`, { params: { lat, lng } })
        .then(({ data }) => {
          const address =
            data?.display_name ||
            data?.formatted_address ||
            data?.results?.[0]?.formatted_address;
          if (address && address !== "Current location") {
            broadcastLocation(address, coords);
          } else {
            broadcastLocation("Current location", coords);
          }
        })
        .catch(() => {
          broadcastLocation("Current location", coords);
        });
    } catch {}
  }, [location_, broadcastLocation]);

  const saveAddress = useCallback(async (addr) => {
    if (!addr || savingAddress) return;
    setSavingAddress(true);
    try {
      const rawCoords = localStorage.getItem("pillstackLocationCoords");
      let coords = null;
      try { coords = rawCoords ? JSON.parse(rawCoords) : null; } catch {}
      await axios.post(
        `${API_BASE}/patient/addDeliveryAddress`,
        { address: addr, ...(coords ? { coords } : {}) },
        { withCredentials: true }
      );
      toast({
        title: "Address saved",
        description: "This address has been added to your saved addresses.",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      setLastSelectedAddress(null);
      window.dispatchEvent(new Event("pillstackAddressSaved"));
    } catch {
      toast({
        title: "Couldn't save address",
        status: "error",
        duration: 2500,
        isClosable: true,
      });
    } finally {
      setSavingAddress(false);
    }
  }, [savingAddress, toast]);

  const searchPlaces = useCallback((query) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.trim().length < 2) {
      setPlaceSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setPlacesLoading(true);
      try {
        const { data } = await axios.get(`${API_BASE}/api/google/places-autocomplete`, {
          params: { input: query.trim() },
        });
        setPlaceSuggestions(data?.predictions || []);
      } catch {
        setPlaceSuggestions([]);
      } finally {
        setPlacesLoading(false);
      }
    }, 300);
  }, []);

  const selectPlace = useCallback(async (placeId, description) => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/google/place-details`, {
        params: { place_id: placeId },
      });
      const address = data?.formatted_address || description;
      const coords = data?.lat && data?.lng ? [data.lng, data.lat] : null;
      broadcastLocation(address, coords);
    } catch {
      broadcastLocation(description, null);
    }
    setLocDraft("");
    setPlaceSuggestions([]);
  }, [broadcastLocation]);

  const selectSavedAddress = useCallback((addr) => {
    const text = addr?.address || addr;
    const coords = addr?.coords ? [addr.coords[0] ?? addr.coords.lng, addr.coords[1] ?? addr.coords.lat] : null;
    broadcastLocation(text, coords);
    setLocDraft("");
    setPlaceSuggestions([]);
    setLocEditing(false);
  }, [broadcastLocation]);

  const resetToCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) return;
    setDetectingGps(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const gpsCoords = [pos.coords.longitude, pos.coords.latitude];
        // Show temporary locating state while reverse geocoding
        broadcastLocation("Locating address...", gpsCoords);
        try {
          const { data } = await axios.get(`${API_BASE}/api/mappls/rev-geocode`, {
            params: { lat: pos.coords.latitude, lng: pos.coords.longitude },
          });
          const address =
            data?.display_name ||
            data?.formatted_address ||
            data?.results?.[0]?.formatted_address;
          if (address && address !== "Current location") {
            broadcastLocation(address, gpsCoords);
          } else {
            // If API returned empty/invalid, show a readable fallback
            broadcastLocation("Current location", gpsCoords);
          }
        } catch {
          // Only show "Current location" on actual API failure
          broadcastLocation("Current location", gpsCoords);
        }
        setDetectingGps(false);
        setLocDraft("");
        setPlaceSuggestions([]);
      },
      () => {
        setDetectingGps(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [broadcastLocation]);

  useEffect(() => {
    let ignore = false;
    const fetchRecentOrder = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/order/recent`, { withCredentials: true });
        if (ignore) return;
        const shouldShow =
          Boolean(data?._id) &&
          data?.status !== "Delivered" &&
          data?.status !== "Cancelled" &&
          (
            ACTIVE_ORDER_STAGES.has(String(data?.workflowStage || "")) ||
            (data?.isPrescriptionOrder && !data?.workflowStage && data?.status === "Processing")
          );
        setActiveOrder(shouldShow ? data : null);
      } catch {
        if (!ignore) setActiveOrder(null);
      }
    };
    fetchRecentOrder();
    return () => {
      ignore = true;
    };
  }, [location.pathname]);

  const openLocEditor = useCallback(() => {
    setLocDraft("");
    setPlaceSuggestions([]);
    setLastSelectedAddress(null);
    setLocEditing(true);
    setTimeout(() => inputRef.current?.focus(), 80);
  }, []);


  const onSearch = (e) => {
    if (e.key === "Enter" && globalSearch.trim()) {
      navigate(`/medicine`, { state: { q: globalSearch.trim() } });
    }
  };

  const isPharmacistView = location.pathname.includes("/pharmacist");

  return (
    <Box
      position="sticky"
      top={0}
      zIndex={1200}
      bg="rgba(255,255,255,0.9)"
      backdropFilter="blur(18px)"
      borderBottom={`1px solid ${colors.neutral[200]}`}
      boxShadow={shadows.sm}
      ml={isPharmacistView ? 0 : { base: 0, md: sidebarOffset }}
      transition="margin-left 0.28s ease"
    >
      <Box px={{ base: "20px", md: "28px", xl: "36px" }}>
        <Box maxW="1560px" mx="auto">
      <Flex
        minH={{ base: "auto", md: "74px" }}
        py={{ base: 3, md: 0 }}
        align="center"
        gap={3}
      >
        {/* LEFT: Logo + Location */}
        <Flex
          direction={{ base: "column", md: "row" }}
          align={{ base: "flex-start", md: "center" }}
          gap={{ base: 0.5, md: 3 }}
          flex="1"
          minW={0}
        >
          <Box
            cursor="pointer"
            onClick={() => navigate(homeRoute)}
            userSelect="none"
          >
            <Text
              fontSize={{ base: "22px", md: "28px" }}
              fontWeight="900"
              letterSpacing="-0.04em"
              bgGradient={gradients.brand}
              bgClip="text"
              color="transparent"
              m={0}
              lineHeight="1"
            >
              PILLSTACK
            </Text>
          </Box>
          {!isPharmacistView && (
            <Popover isOpen={locEditing} onClose={() => { setLocEditing(false); setPlaceSuggestions([]); }} placement="bottom-start">
              <PopoverTrigger>
                <HStack
                  as="button"
                  spacing={1}
                  onClick={openLocEditor}
                  color={colors.neutral[500]}
                  _hover={{ color: colors.brand[600] }}
                  transition="color 0.15s"
                  maxW={{ base: "100%", sm: "100%", md: "360px" }}
                >
                  <Icon as={FiMapPin} boxSize={3} color={colors.brand[500]} flexShrink={0} />
                  <Text fontSize="11px" fontWeight="600" m={0} noOfLines={1} color="inherit">
                    {location_ || "Set location"}
                  </Text>
                  <Icon as={FiChevronDown} boxSize={3} flexShrink={0} />
                </HStack>
              </PopoverTrigger>
              <PopoverContent borderRadius={radii.md} border={`1px solid ${colors.neutral[200]}`} boxShadow={shadows.lg} w="360px">
                <PopoverBody p={0}>
                  <Stack spacing={0}>
                    <Box px={3} pt={3} pb={2}>
                      <InputGroup size="sm">
                        <InputLeftElement pointerEvents="none">
                          <Icon as={FiSearch} color={colors.neutral[400]} boxSize={3.5} />
                        </InputLeftElement>
                        <Input
                          ref={inputRef}
                          value={locDraft}
                          onChange={(e) => { setLocDraft(e.target.value); searchPlaces(e.target.value); }}
                          placeholder="Search for area, street, locality..."
                          borderRadius="10px"
                          bg={colors.neutral[50]}
                          _focus={{ borderColor: colors.brand[400], boxShadow: `0 0 0 1px ${colors.brand[400]}` }}
                        />
                      </InputGroup>
                    </Box>
                    <Box
                      as="button" type="button" onClick={resetToCurrentLocation}
                      display="flex" alignItems="center" gap={2.5} w="100%" px={4} py={2.5}
                      bg="transparent" _hover={{ bg: colors.brand[50] }} transition="background 0.12s"
                      borderTop={`1px solid ${colors.neutral[100]}`} cursor="pointer" disabled={detectingGps}
                    >
                      <Icon as={FiCrosshair} color={colors.brand[600]} boxSize={4} />
                      <Box textAlign="left">
                        <Text fontSize="13px" fontWeight="700" color={colors.brand[700]} m={0}>
                          {detectingGps ? "Detecting..." : "Use current location"}
                        </Text>
                        <Text fontSize="11px" color={colors.neutral[500]} m={0}>Using GPS</Text>
                      </Box>
                    </Box>
                    {locDraft.trim().length >= 2 && (
                      <Box borderTop={`1px solid ${colors.neutral[100]}`}>
                        <Text fontSize="10px" fontWeight="800" color={colors.neutral[400]} textTransform="uppercase" letterSpacing="0.08em" m={0} px={4} pt={2} pb={1}>
                          Search results
                        </Text>
                        <Stack spacing={0} maxH="200px" overflowY="auto">
                          {placesLoading ? (
                            <Text fontSize="12px" color={colors.neutral[500]} px={4} py={2} m={0}>Searching...</Text>
                          ) : placeSuggestions.length > 0 ? (
                            placeSuggestions.map((p) => (
                              <Box
                                key={p.place_id} as="button" type="button"
                                onClick={() => selectPlace(p.place_id, p.description)}
                                display="flex" alignItems="center" gap={2.5} w="100%" px={4} py={2}
                                bg="transparent" _hover={{ bg: colors.neutral[50] }} transition="background 0.12s"
                                cursor="pointer" textAlign="left"
                              >
                                <Icon as={FiMapPin} color={colors.neutral[400]} boxSize={3.5} flexShrink={0} />
                                <Text fontSize="12px" fontWeight="600" color={colors.neutral[800]} m={0} noOfLines={2}>
                                  {p.description}
                                </Text>
                              </Box>
                            ))
                          ) : (
                            <Text fontSize="12px" color={colors.neutral[500]} px={4} py={2} m={0}>No results found</Text>
                          )}
                        </Stack>
                      </Box>
                    )}
                    {isLoggedIn && lastSelectedAddress && !isAddressSaved(lastSelectedAddress) && (
                      <Box borderTop={`1px solid ${colors.neutral[100]}`} px={4} py={2.5} bg={colors.brand[50]}>
                        <Flex align="center" gap={2.5}>
                          <Icon as={FiBookmark} color={colors.brand[600]} boxSize={4} flexShrink={0} />
                          <Box flex={1} minW={0}>
                            <Text fontSize="12px" fontWeight="700" color={colors.brand[800]} m={0} noOfLines={1}>{lastSelectedAddress}</Text>
                            <Text fontSize="11px" color={colors.neutral[500]} m={0}>Save for future deliveries?</Text>
                          </Box>
                          <Box
                            as="button" type="button" onClick={() => saveAddress(lastSelectedAddress)}
                            display="flex" alignItems="center" gap={1} px={2.5} py={1} bg="white"
                            border={`1px solid ${colors.brand[200]}`} borderRadius="8px" fontSize="11px"
                            fontWeight="800" color={colors.brand[700]} cursor="pointer"
                            _hover={{ bg: colors.brand[100] }} transition="all 0.12s" flexShrink={0} disabled={savingAddress}
                          >
                            <Icon as={savingAddress ? FiCheck : FiBookmark} boxSize={3} />
                            {savingAddress ? "Saving..." : "Save"}
                          </Box>
                        </Flex>
                      </Box>
                    )}
                    {savedAddresses.length > 0 && (
                      <Box borderTop={`1px solid ${colors.neutral[100]}`}>
                        <Text fontSize="10px" fontWeight="800" color={colors.neutral[400]} textTransform="uppercase" letterSpacing="0.08em" m={0} px={4} pt={2} pb={1}>
                          Saved addresses
                        </Text>
                        <Stack spacing={0} maxH="160px" overflowY="auto" pb={1}>
                          {savedAddresses.map((addr, i) => (
                            <Box
                              key={`saved-${i}`} as="button" type="button" onClick={() => selectSavedAddress(addr)}
                              display="flex" alignItems="center" gap={2.5} w="100%" px={4} py={2}
                              bg="transparent" _hover={{ bg: colors.neutral[50] }} transition="background 0.12s"
                              cursor="pointer" textAlign="left"
                            >
                              <Icon as={FiClock} color={colors.neutral[400]} boxSize={3.5} flexShrink={0} />
                              <Text fontSize="12px" fontWeight="600" color={colors.neutral[800]} m={0} noOfLines={1}>
                                {addr?.address || addr}
                              </Text>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          )}
        </Flex>

        {/* RIGHT: Auth + Cart */}
        <HStack spacing={{ base: 3, md: 5 }} flexShrink={0} align="center">
          {/* Active order indicator */}
          {activeOrder && (
            <Box
              as="button"
              onClick={() => navigate("/orderDetails")}
              px={3}
              py={1.5}
              borderRadius="full"
              border={`1px solid ${colors.success[200]}`}
              bg={colors.success[50]}
              color={colors.success[700]}
              fontWeight="700"
              fontSize="12px"
              display={{ base: "none", lg: "flex" }}
              alignItems="center"
              gap={1.5}
              _hover={{ bg: colors.success[100] }}
              transition="all 0.15s"
            >
              <Icon as={FiPackage} boxSize={3.5} />
              <Text m={0}>In progress</Text>
            </Box>
          )}

          {/* Pharmacist link (desktop only) */}
          {!isLoggedIn && !isPharmacistView && (
            <Box
              as="button"
              onClick={() => navigate("/login")}
              display={{ base: "none", md: "flex" }}
              flexDirection="column"
              alignItems="center"
              gap={0.5}
              color={colors.neutral[500]}
              _hover={{ color: colors.brand[600] }}
              transition="color 0.15s"
            >
              <Icon as={FiBriefcase} boxSize={5} />
              <Text fontSize="10px" fontWeight="600" m={0} whiteSpace="nowrap">Pharmacist</Text>
            </Box>
          )}

          {/* Notifications */}
          <Popover isOpen={notifOpen} onClose={() => setNotifOpen(false)} placement="bottom-end">
            <PopoverTrigger>
              <Box
                as="button"
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={0.5}
                color={unread > 0 ? colors.brand[700] : colors.neutral[600]}
                _hover={{ color: colors.brand[600] }}
                transition="color 0.15s"
                onClick={() => {
                  setNotifOpen(o => !o);
                  setPushState(prev => ({ ...prev, permission: getPushPermission() }));
                }}
              >
                <Box position="relative" h="20px" display="flex" alignItems="center">
                  <Icon as={FiBell} boxSize={5} />
                  {unread > 0 && (
                    <Box
                      position="absolute"
                      top="-6px"
                      right="-6px"
                      minW="16px"
                      h="16px"
                      px={0.5}
                      bg={colors.accent[500]}
                      color="white"
                      fontSize="9px"
                      fontWeight="900"
                      borderRadius="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      border="2px solid white"
                    >
                      {unread > 9 ? "9+" : unread}
                    </Box>
                  )}
                </Box>
                <Text fontSize="10px" fontWeight="600" m={0} whiteSpace="nowrap">Alerts</Text>
              </Box>
            </PopoverTrigger>
            <PopoverContent w="340px" borderRadius={radii.md} border={`1px solid ${colors.neutral[200]}`} boxShadow={shadows.lg}>
              <PopoverBody p={0}>
                <Flex align="center" justify="space-between" px={3} py={2} borderBottom={`1px solid ${colors.neutral[100]}`}>
                  <Text fontSize="13px" fontWeight="700" m={0} color={colors.neutral[800]}>Notifications</Text>
                  <HStack spacing={1}>
                    {pushState.supported && pushState.permission !== 'granted' && pushState.permission !== 'denied' && (
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="brand"
                        onClick={handleEnablePush}
                        isLoading={pushState.subscribing}
                        fontSize="11px"
                      >
                        Enable push
                      </Button>
                    )}
                    {pushState.supported && pushState.permission === 'denied' && (
                      <Text fontSize="10px" color={colors.neutral[500]}>Push blocked</Text>
                    )}
                    {unread > 0 && (
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="brand"
                        onClick={markAllRead}
                        fontSize="11px"
                      >
                        Mark all read
                      </Button>
                    )}
                  </HStack>
                </Flex>
                <Stack spacing={0} maxH="360px" overflowY="auto">
                  {localNotifs.length > 0 ? localNotifs.map((notif) => (
                    <Flex
                      key={notif._id}
                      direction="column"
                      gap={1}
                      px={3}
                      py={2.5}
                      borderBottom={`1px solid ${colors.neutral[50]}`}
                      bg={notif.isRead ? 'transparent' : colors.brand[50]}
                      cursor="pointer"
                      onClick={() => {
                        if (!notif.isRead) markRead(notif._id);
                        if (notif.data?.orderId) navigate(`/order/prescription/${notif.data.orderId}`);
                      }}
                    >
                      <Flex align="flex-start" justify="space-between" gap={2}>
                        <Box flex={1} minW={0}>
                          <Text fontSize="12px" fontWeight={notif.isRead ? 500 : 700} m={0} color={colors.neutral[800]} noOfLines={1}>
                            {notif.title}
                          </Text>
                          <Text fontSize="11px" m={0} color={colors.neutral[600]} noOfLines={2}>
                            {notif.message}
                          </Text>
                          <Text fontSize="10px" m={0} color={colors.neutral[400]} mt={0.5}>
                            {new Date(notif.createdAt).toLocaleString()}
                          </Text>
                        </Box>
                        <IconButton
                          aria-label="Dismiss"
                          icon={<Icon as={FiX} boxSize={3} />}
                          variant="ghost"
                          colorScheme="red"
                          size="xs"
                          flexShrink={0}
                          onClick={(e) => { e.stopPropagation(); deleteNotification(notif._id); }}
                        />
                      </Flex>
                    </Flex>
                  )) : (
                    <Text fontSize="13px" color={colors.neutral[400]} textAlign="center" py={6} m={0}>No notifications</Text>
                  )}
                </Stack>
              </PopoverBody>
            </PopoverContent>
          </Popover>

          {/* Cart */}
          {!isPharmacistView && (
            <Box
              as="button"
              type="button"
              onClick={() => navigate("/home")}
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={0.5}
              color={colors.neutral[600]}
              _hover={{ color: colors.accent[600] }}
              transition="color 0.15s"
            >
              <Box position="relative" h="20px" display="flex" alignItems="center">
                <Icon as={FiShoppingCart} boxSize={5} strokeWidth="2.5" />
                {cartCount > 0 && (
                  <Box
                    position="absolute"
                    top="-6px"
                    right="-6px"
                    minW="16px"
                    h="16px"
                    px={0.5}
                    bg={colors.accent[500]}
                    color="white"
                    fontSize="9px"
                    fontWeight="900"
                    borderRadius="full"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    border="2px solid white"
                  >
                    {cartCount > 9 ? "9+" : cartCount}
                  </Box>
                )}
              </Box>
              <Text fontSize="10px" fontWeight="600" m={0} whiteSpace="nowrap">Cart</Text>
            </Box>
          )}


          {/* Login / Profile */}
          {isLoggedIn ? (
            <Menu placement="bottom-end">
              <MenuButton as={Box} cursor="pointer">
                <Flex
                  flexDirection="column"
                  alignItems="center"
                  gap={0.5}
                  color={colors.neutral[600]}
                  _hover={{ color: colors.brand[600] }}
                  transition="color 0.15s"
                >
                <Box position="relative" h="20px" display="flex" alignItems="center">
                  <Box
                    w="20px"
                    h="20px"
                    borderRadius="full"
                    bgGradient={gradients.brand}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <Text fontSize="8px" fontWeight="800" color="white" m={0} lineHeight="1">
                      {(name || username || "A").slice(0, 2).toUpperCase()}
                    </Text>
                  </Box>
                </Box>
                <Text fontSize="10px" fontWeight="600" m={0} noOfLines={1} maxW="56px" color="inherit">
                  {name || username || "Account"}
                </Text>
                </Flex>
              </MenuButton>
              <MenuList
                borderRadius={radii.md}
                border={`1px solid ${colors.neutral[200]}`}
                boxShadow={shadows.xl}
                py={2}
                minW="200px"
                maxW="280px"
              >
                <Box px={4} py={3} borderBottom={`1px solid ${colors.neutral[100]}`}>
                  <Text fontWeight="800" color={colors.neutral[900]} m={0} noOfLines={1}>
                    {name || username || "Patient"}
                  </Text>
                  <Text fontSize="11px" color={colors.neutral[500]} m={0}>
                    @{username || "—"}
                  </Text>
                </Box>
                {cookies.role !== "pharmacist" && (
                  <MenuGroup title="Account">
                    <MenuItem borderRadius="8px" mx={2} w="calc(100% - 16px)" onClick={() => navigate("/home", { state: { openPrescriptions: true } })}>My Prescriptions</MenuItem>
                    <MenuItem borderRadius="8px" mx={2} w="calc(100% - 16px)" onClick={() => navigate("/orderDetails")}>My Orders</MenuItem>
                    <MenuItem borderRadius="8px" mx={2} w="calc(100% - 16px)" onClick={() => navigate("/home", { state: { openAddresses: true } })}>Saved Addresses</MenuItem>
                  </MenuGroup>
                )}
                {cookies.role !== "pharmacist" && <MenuDivider />}
                <MenuGroup title="Settings">
                  <MenuItem borderRadius="8px" mx={2} w="calc(100% - 16px)" color={colors.error[600]} onClick={onLogout}>
                    Log Out
                  </MenuItem>
                </MenuGroup>
              </MenuList>
            </Menu>
          ) : (
            <Box
              as="button"
              onClick={onLoginOpen}
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={0.5}
              color={colors.neutral[600]}
              _hover={{ color: colors.brand[600] }}
              transition="color 0.15s"
            >
              <Icon as={FiUser} boxSize={5} />
              <Text fontSize="10px" fontWeight="600" m={0} whiteSpace="nowrap">Login</Text>
            </Box>
          )}
        </HStack>
      </Flex>
        </Box>
      </Box>

      {/* Patient login popup */}
      <Modal isOpen={isLoginOpen} onClose={onLoginClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="16px" p={2}>
          <ModalHeader textAlign="center" borderBottom="none" pt={6}>
            <Text fontSize="20px" fontWeight="800" color={colors.neutral[900]}>Sign in to continue</Text>
            <Text fontSize="13px" color={colors.neutral[500]} fontWeight="normal" mt={1}>
              Enter your phone number to get a one-time password.
            </Text>
          </ModalHeader>
          <ModalCloseButton mt={4} mr={4} />
          <ModalBody pb={6}>
            <LoginForm
              showLinks={false}
              onSuccess={({ role } = {}) => {
                onLoginClose();
                if (role === "pharmacist") window.location.replace("/pharmacist/home");
                else if (role === "admin") window.location.replace("/admin-home");
                else window.location.reload();
              }}
            />
            <Text fontSize="12px" color={colors.neutral[400]} textAlign="center" mt={4}>
              Signing in allows you to save addresses, track orders, and get the best prices from nearby pharmacies.
            </Text>
            <Text fontSize="12px" color={colors.neutral[500]} textAlign="center" mt={3} m={0}>
              Are you a pharmacist?{" "}
              <Box
                as="span"
                color={colors.brand[600]}
                fontWeight="700"
                cursor="pointer"
                onClick={() => { onLoginClose(); navigate("/login"); }}
                _hover={{ textDecoration: "underline" }}
              >
                Login as Pharmacist
              </Box>
            </Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default WithSubnavigation;
